import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/lib/components/ui/button";
import { Input } from "@/lib/components/ui/input";
import { Badge } from "@/lib/components/ui/badge";
import { Card } from "@/lib/components/ui/card";
import { Skeleton } from "@/lib/components/ui/skeleton";
import { fetchProducts, fetchCategories, addProductToCart } from "@/lib/catalogApi";
import { useCart } from "@/features/cart/hooks/useCart";
import { formatCurrency } from "@/lib/format";
import type { Product, ProductCategory } from "@/types/catalog";

const SKELETON_COUNT = 8;

function ProductCardSkeleton() {
  return (
    <Card className="overflow-hidden bg-card/90 border-border shadow-sm p-0">
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-4 w-3/4" />
        <div className="flex gap-1">
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-10 w-full" />
      </div>
    </Card>
  );
}

export default function ProductsPage() {
  const { t } = useTranslation("catalog");
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const { increment } = useCart();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProducts({ category_id: selectedCategoryId ?? undefined, search: search || undefined }),
      fetchCategories(),
    ])
      .then(([prods, cats]) => {
        setProducts(prods);
        setCategories(cats);
      })
      .catch(() => {
        toast.error(t("products.failedToLoad"));
      })
      .finally(() => setLoading(false));
  }, [selectedCategoryId, search, t]);

  const getQty = (id: number) => quantities[id] ?? 1;

  const handleAddToCart = async (product: Product) => {
    const qty = getQty(product.id);
    try {
      await addProductToCart(product.id, qty);
      increment(qty);
      toast.success(t("products.addedToCart", { name: product.name }));
    } catch {
      toast.error(t("products.failedToAdd"));
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
      {/* Hero */}
      <section className="px-6 pt-12 pb-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{t("products.title")}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            {t("products.description")}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 pb-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("products.searchPlaceholder")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Category filter pills: skeleton on initial load, real buttons once categories exist */}
          <div className="flex flex-wrap gap-2">
            {loading && categories.length === 0 ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-16 rounded-full" />
              ))
            ) : (
              <>
                <Button
                  variant={selectedCategoryId === null ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => setSelectedCategoryId(null)}
                >
                  {t("products.all")}
                </Button>
                {categories.map(cat => (
                  <Button
                    key={cat.id}
                    variant={selectedCategoryId === cat.id ? "default" : "outline"}
                    size="sm"
                    className="rounded-full"
                    onClick={() => setSelectedCategoryId(cat.id)}
                  >
                    {cat.name}
                  </Button>
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">{t("products.noResults")}</p>
          ) : (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map(product => {
                const primaryImage = product.images?.[0];
                const qty = getQty(product.id);
                return (
                  <Card
                    key={product.id}
                    className="overflow-hidden bg-card/90 border-border shadow-sm hover:shadow-md transition-shadow p-0"
                  >
                    <Link to={`/products/${product.slug}`} className="block">
                      <div className="relative aspect-square bg-muted">
                        {primaryImage ? (
                          <img
                            src={primaryImage.url}
                            alt={primaryImage.alt ?? product.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                            {t("products.noImage")}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <h2 className="text-base font-semibold text-white drop-shadow line-clamp-2">
                            {product.name}
                          </h2>
                        </div>
                      </div>
                    </Link>
                    <div className="p-4 space-y-3">
                      {product.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {product.categories.map(cat => (
                            <Badge key={cat.id} variant="outline" className="text-xs">
                              {cat.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold">
                          {formatCurrency(product.gross_price)}
                        </span>
                        <span className="text-xs text-muted-foreground">{t("products.inclVat")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          value={qty}
                          onChange={e =>
                            setQuantities(q => ({
                              ...q,
                              [product.id]: Math.max(1, parseInt(e.target.value) || 1),
                            }))
                          }
                          className="w-16 text-center"
                        />
                        <Button className="flex-1" onClick={() => handleAddToCart(product)}>
                          {t("products.addToCart")}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
